import axios from 'axios'

const api = axios.create({
  baseURL: '/v1',
  timeout: 30000,
})

function getTokenForCurrentPage(): string | null {
  const path = window.location.pathname
  if (path.startsWith('/sender') || path.includes('/sender/')) return localStorage.getItem('sender_token')
  if (path.startsWith('/worker') || path.includes('/worker/')) return localStorage.getItem('worker_token')
  return localStorage.getItem('customer_token') || localStorage.getItem('admin_token')
}

api.interceptors.request.use((config) => {
  const token = getTokenForCurrentPage()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const status = error.response?.status
    const message = error.response?.data?.message || '请求失败'

    // 401: Token 过期或无效，自动清除并刷新页面
    if (status === 401) {
      const path = window.location.pathname
      if (path.startsWith('/sender')) {
        localStorage.removeItem('sender_token')
      } else if (path.startsWith('/worker')) {
        localStorage.removeItem('worker_token')
      } else {
        localStorage.removeItem('customer_token')
      }
      // 延迟刷新避免无限循环
      setTimeout(() => window.location.reload(), 100)
      return Promise.reject(new Error('登录已过期，请重新登录'))
    }

    return Promise.reject(new Error(message))
  }
)

export default api

// 订单相关API
export const orderApi = {
  getPublic: (orderNo: string): Promise<unknown> => api.get(`/orders/public/${orderNo}`),
  get: (orderNo: string): Promise<unknown> => api.get(`/orders/${orderNo}`),
  updateStatus: (orderNo: string, data: unknown): Promise<unknown> => api.post(`/orders/${orderNo}/status`, data),
  modifyAddress: (orderNo: string, data: unknown): Promise<unknown> => api.post(`/orders/${orderNo}/address/modify`, data),
  submitAddress: (orderNo: string, data: unknown): Promise<unknown> => api.post(`/orders/${orderNo}/address`, data),
  createShare: (orderNo: string, data?: unknown): Promise<unknown> => api.post(`/orders/${orderNo}/share`, data || {}),
  revokeShare: (orderNo: string, tokenId: string): Promise<unknown> => api.post(`/orders/${orderNo}/share/${tokenId}/revoke`),
  submitBatch: (orderNo: string, data: unknown): Promise<unknown> => api.post(`/orders/${orderNo}/batches`, data),
  confirmDefect: (orderNo: string, batchIndex: number, decision: 'continue' | 'replace' | 'consult'): Promise<unknown> =>
    api.post(`/orders/${orderNo}/batches/${batchIndex}/defect/confirm`, { decision }),
}

// 认证相关API
export const authApi = {
  sendSms: (phone: string): Promise<unknown> => api.post('/auth/send-sms', { phone }),
  verifyPhone: (data: unknown): Promise<unknown> => api.post('/auth/verify-phone', data),
  workerLogin: (phone: string, password: string): Promise<unknown> =>
    api.post('/auth/worker-login', { phone, password }),
  me: (): Promise<unknown> => api.get('/auth/me'),
  agreeTerms: (version?: string): Promise<unknown> => api.post('/auth/agree-terms', { version }),
}

// 客户相关API
export const customerApi = {
  getOrders: (): Promise<unknown> => api.get('/customer/orders'),
  bindOrder: (orderNo: string): Promise<unknown> => api.post(`/customer/orders/${orderNo}/bind`),
}

// 寄件人相关API
export const senderApi = {
  getOrder: (token: string): Promise<unknown> => api.get(`/sender/orders/${token}`),
  submitShipment: (token: string, data: unknown): Promise<unknown> => api.post(`/sender/orders/${token}/batches`, data),
  submitAddress: (token: string, data: unknown): Promise<unknown> => api.post(`/sender/orders/${token}/address`, data),
}

// 加工端相关API（v2.1: JWT认证，workerId从token中读取）
export const workerApi = {
  scan: (trackingNo: string): Promise<unknown> => api.post('/worker/scan', { trackingNo }),
  getOrders: (): Promise<unknown> => api.get('/worker/orders'),
  receive: (orderNo: string, batchIndex: number): Promise<unknown> =>
    api.post(`/worker/orders/${orderNo}/batches/${batchIndex}/receive`),
  start: (orderNo: string, batchIndex: number): Promise<unknown> =>
    api.post(`/worker/orders/${orderNo}/batches/${batchIndex}/start`),
  complete: (orderNo: string, batchIndex: number, data?: unknown): Promise<unknown> =>
    api.post(`/worker/orders/${orderNo}/batches/${batchIndex}/complete`, data || {}),
  exception: (orderNo: string, batchIndex: number, data: Record<string, unknown>): Promise<unknown> =>
    api.post(`/worker/orders/${orderNo}/batches/${batchIndex}/exception`, data),
  recover: (orderNo: string, batchIndex: number): Promise<unknown> =>
    api.post(`/worker/orders/${orderNo}/batches/${batchIndex}/recover`),
  reportDefect: (orderNo: string, batchIndex: number, data: Record<string, unknown>): Promise<unknown> =>
    api.post(`/worker/orders/${orderNo}/batches/${batchIndex}/defect`, data),
}

// 发货端相关API
export const deliverApi = {
  scanOutbound: (orderNo: string, data: unknown): Promise<unknown> => api.post(`/deliver/orders/${orderNo}/scan-outbound`, data),
  markEcommerceShipped: (orderNo: string): Promise<unknown> => api.post(`/deliver/orders/${orderNo}/mark-ecommerce-shipped`),
}

// 物流相关API
export const shipmentApi = {
  queryTracking: (trackingNo: string, carrier?: string): Promise<unknown> =>
    api.get('/shipments/tracking', { params: { trackingNo, carrier } }),
}

// 公开配置
export const configApi = {
  getPublic: (): Promise<{ thirdPartyShipping: boolean }> => api.get('/config/public'),
}
